import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Loader2, Plus, Trash2, Link as LinkIcon } from "lucide-react";
import { toast } from "sonner";

export default function InternalLinkManager() {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [keyword, setKeyword] = useState("");
  const [targetUrl, setTargetUrl] = useState("");
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    fetchLinks();
  }, []);

  const fetchLinks = async () => {
    try {
      const res = await axios.get(`${API}/seo/internal-links`, { withCredentials: true });
      setLinks(res.data);
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch internal links");
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!keyword || !targetUrl) return;
    
    setAdding(true);
    try {
      await axios.post(
        `${API}/seo/internal-links`,
        { keyword, target_url: targetUrl },
        { withCredentials: true }
      );
      toast.success("Internal link rule created!");
      setKeyword("");
      setTargetUrl("");
      fetchLinks();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || "Failed to create rule");
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (linkId) => {
    if (!window.confirm("Delete this internal link rule?")) return;
    try {
      await axios.delete(`${API}/seo/internal-links/${linkId}`, { withCredentials: true });
      toast.success("Rule deleted");
      fetchLinks();
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete rule");
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-emerald-600 dark:text-emerald-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="p-6 bg-white dark:bg-[#0B101A] border border-gray-100 dark:border-white/[0.05] rounded-xl">
        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <LinkIcon className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
          Create Auto-Link Rule
        </h3>
        <p className="text-sm text-gray-500 dark:text-[#94A3B8] mb-4">
          Whenever the keyword appears in a blog post, it will automatically become a clickable link pointing to the target URL.
        </p>
        <form onSubmit={handleAdd} className="flex items-end gap-4">
          <div className="flex-1 space-y-2">
            <Label className="text-gray-500 dark:text-[#94A3B8]">Keyword (exact match)</Label>
            <Input 
              placeholder="e.g. Drishti AI" 
              value={keyword} 
              onChange={e => setKeyword(e.target.value)}
              className="bg-white dark:bg-[#F3F4F6] dark:bg-[#050505] border-gray-200 dark:border-white/[0.1] text-gray-900 dark:text-white"
            />
          </div>
          <div className="flex-1 space-y-2">
            <Label className="text-gray-500 dark:text-[#94A3B8]">Target URL</Label>
            <Input 
              placeholder="e.g. /drishti-ai" 
              value={targetUrl} 
              onChange={e => setTargetUrl(e.target.value)}
              className="bg-white dark:bg-[#F3F4F6] dark:bg-[#050505] border-gray-200 dark:border-white/[0.1] text-gray-900 dark:text-white"
            />
          </div>
          <Button type="submit" disabled={adding} className="bg-emerald-600 hover:bg-emerald-700 text-white font-medium shadow-xs rounded-xl h-10 px-4">
            {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
            Add Rule
          </Button>
        </form>
      </div>

      <div className="rounded-xl border border-gray-100 dark:border-white/[0.05] overflow-hidden bg-white dark:bg-[#0B101A]">
        <Table>
          <TableHeader className="bg-black/40">
            <TableRow className="border-gray-100 dark:border-white/[0.05] hover:bg-transparent">
              <TableHead className="text-gray-500 dark:text-[#94A3B8]">Keyword</TableHead>
              <TableHead className="text-gray-500 dark:text-[#94A3B8]">Target URL</TableHead>
              <TableHead className="text-gray-500 dark:text-[#94A3B8]">Status</TableHead>
              <TableHead className="text-gray-500 dark:text-[#94A3B8] text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {links.length === 0 ? (
              <TableRow className="border-gray-100 dark:border-white/[0.05] hover:bg-transparent">
                <TableCell colSpan={4} className="text-center text-gray-500 dark:text-[#94A3B8] py-8">
                  No internal link rules configured.
                </TableCell>
              </TableRow>
            ) : (
              links.map((l) => (
                <TableRow key={l.link_id} className="border-gray-100 dark:border-white/[0.05] hover:bg-gray-50 dark:bg-white/[0.02]">
                  <TableCell className="font-medium text-gray-900 dark:text-white">{l.keyword}</TableCell>
                  <TableCell className="font-mono text-emerald-600 dark:text-emerald-400 text-sm">{l.target_url}</TableCell>
                  <TableCell>
                    <span className="px-2.5 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800 rounded-full text-xs font-medium">Active</span>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => handleDelete(l.link_id)}
                      className="text-red-400 hover:text-red-300 hover:bg-red-400/10"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
